import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { getCurrentApiEndpoints } from '../../config/api';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  color: #000;
  margin: 0;
  font-weight: 600;
`;

const NewRequestButton = styled(Link)`
  background: #000;
  color: white;
  text-decoration: none;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: #333;
    transform: translateY(-1px);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  background: white;
  border-radius: 20px;
  border: 2px solid #000;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
`;

const EmptyIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const RequestsGrid = styled.div`
  display: grid;
  gap: 1.5rem;
`;

const RequestCard = styled.div`
  background: white;
  border-radius: 20px;
  border: 2px solid #000;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
  overflow: hidden;
`;

const RequestHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1.5rem;
  border-bottom: 1px solid #f0f0f0;
`;

const CompanyName = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
  color: #000;
  margin-bottom: 0.25rem;
`;

const RequestId = styled.div`
  color: #666;
  font-size: 0.9rem;
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 500;
  
  ${props => {
    switch (props.status) {
      case 'draft':
        return 'background: #e9ecef; color: #495057;';
      case 'submitted':
        return 'background: #d1ecf1; color: #0c5460;';
      case 'awaiting_payment':
        return 'background: #fff3cd; color: #856404;';
      case 'payment_verification':
        return 'background: #e2e3e5; color: #383d41;';
      case 'in_progress':
        return 'background: #cce5ff; color: #004085;';
      case 'awaiting_documents':
        return 'background: #fff3cd; color: #856404;';
      case 'awaiting_signatures':
        return 'background: #d1ecf1; color: #0c5460;';
      case 'completed':
        return 'background: #d4edda; color: #155724;';
      case 'rejected':
        return 'background: #f8d7da; color: #721c24;';
      default:
        return 'background: #e9ecef; color: #495057;';
    }
  }}
`;

const RequestBody = styled.div`
  padding: 1.5rem;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1.5rem;
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const InfoLabel = styled.div`
  font-size: 0.875rem;
  color: #000;
  font-weight: 500;
`;

const InfoValue = styled.div`
  color: #000;
  font-weight: 500;
`;

const OwnersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const OwnerItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  background: #f8f9fa;
  border-radius: 15px;
  border-left: 3px solid #000;
`;

const OwnerName = styled.div`
  font-weight: 500;
  color: #000;
`;

const OwnerShare = styled.div`
  background: #000;
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
  font-size: 0.875rem;
  font-weight: 500;
`;

const ProgressSection = styled.div`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #f0f0f0;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: #e9ecef;
  border-radius: 15px;
  overflow: hidden;
  margin-bottom: 0.5rem;
`;

const ProgressFill = styled.div<{ percentage: number }>`
  height: 100%;
  background: #000;
  width: ${props => props.percentage}%;
  transition: width 0.3s ease;
`;

const ProgressText = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.875rem;
  color: #666;
`;

const ViewButton = styled(Link)`
  display: inline-block;
  background: #000;
  color: white;
  text-decoration: none;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  font-weight: 500;
  text-align: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: #333;
    transform: translateY(-1px);
  }
`;

const ContinueButton = styled(Link)`
  display: inline-block;
  background: #42b72a;
  color: white;
  text-decoration: none;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  font-weight: 500;
  text-align: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: #36a420;
    transform: translateY(-1px);
  }
`;

interface UserRegistration {
  id: number;
  status: string;
  company_name_armenian: string;
  company_name_english: string;
  business_activity: string;
  business_activity_code: string;
  owners_data: string;
  authorized_capital: number;
  created_at: string;
  updated_at: string;
  progress_percentage: number;
}

interface Owner {
  full_name: string;
  address: string;
  passport_scan_path?: string;
  additional_proof_path?: string;
  share_percentage: number;
}

const MyRequests: React.FC = () => {
  const [registrations, setRegistrations] = useState<UserRegistration[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchRegistrations = async () => {
      try {
        const response = await axios.get(getCurrentApiEndpoints().MY_REGISTRATIONS);
        setRegistrations(response.data);
      } catch (error) {
        console.error('Failed to fetch registrations:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRegistrations();
  }, []);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'draft': return 'Draft';
      case 'submitted': return 'Under Review';
      case 'awaiting_payment': return 'Awaiting Payment';
      case 'payment_verification': return 'Payment Verification';
      case 'in_progress': return 'In Progress';
      case 'completed': return 'Completed';
      case 'rejected': return 'Rejected';
      default: return status;
    }
  };

  const getStatusDescription = (status: string) => {
    switch (status) {
      case 'draft': return 'Continue editing your registration';
      case 'submitted': return 'We are reviewing your submission';
      case 'awaiting_payment': return 'Invoice has been sent';
      case 'payment_verification': return 'Payment is being verified';
      case 'in_progress': return 'Registration is being processed';
      case 'completed': return 'Registration is complete';
      case 'rejected': return 'Please review and resubmit';
      default: return '';
    }
  };

  const parseOwners = (ownersData: string | null | undefined): Owner[] => {
    try {
      return ownersData ? JSON.parse(ownersData) : [];
    } catch {
      return [];
    }
  };

  if (isLoading) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          Loading your requests...
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>My Registration Requests</Title>
        <NewRequestButton to="/">+ New Registration</NewRequestButton>
      </Header>

      {registrations.length === 0 ? (
        <EmptyState>
          <EmptyIcon>📄</EmptyIcon>
          <h3>No registration requests yet</h3>
          <p>Start your first company registration by clicking the button above.</p>
        </EmptyState>
      ) : (
        <RequestsGrid>
          {registrations.map((registration) => {
            const owners = parseOwners(registration.owners_data);
            return (
              <RequestCard key={registration.id}>
                <RequestHeader>
                  <div>
                    <CompanyName>
                      {registration.company_name_armenian || registration.company_name_english || 'Unnamed Company'}
                    </CompanyName>
                    <RequestId>Request #{registration.id}</RequestId>
                  </div>
                  <StatusBadge status={registration.status}>
                    {getStatusLabel(registration.status)}
                  </StatusBadge>
                </RequestHeader>

                <RequestBody>
                  <InfoGrid>
                    <InfoSection>
                      <InfoLabel>Business Activity</InfoLabel>
                      <InfoValue>{registration.business_activity || 'Not specified'}</InfoValue>
                    </InfoSection>
                    
                    <InfoSection>
                      <InfoLabel>Authorized Capital</InfoLabel>
                      <InfoValue>
                        {registration.authorized_capital 
                          ? `${registration.authorized_capital.toLocaleString()} AMD`
                          : 'Not specified'}
                      </InfoValue>
                    </InfoSection>
                    
                    <InfoSection>
                      <InfoLabel>Created</InfoLabel>
                      <InfoValue>{formatDate(registration.created_at)}</InfoValue>
                    </InfoSection>
                  </InfoGrid>

                  <InfoSection>
                    <InfoLabel>Owners ({owners.length})</InfoLabel>
                    <OwnersList>
                      {owners.length > 0 ? (
                        owners.map((owner, index) => (
                          <OwnerItem key={index}>
                            <OwnerName>{owner.full_name}</OwnerName>
                            <OwnerShare>{owner.share_percentage}%</OwnerShare>
                          </OwnerItem>
                        ))
                      ) : (
                        <div style={{ color: '#6c757d', fontStyle: 'italic' }}>
                          No owners specified
                        </div>
                      )}
                    </OwnersList>
                  </InfoSection>

                  <ProgressSection>
                    <ProgressText>
                      <span>Progress</span>
                      <span>{registration.progress_percentage}% Complete</span>
                    </ProgressText>
                    <ProgressBar>
                      <ProgressFill percentage={registration.progress_percentage} />
                    </ProgressBar>
                    <div style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.5rem' }}>
                      {getStatusDescription(registration.status)}
                    </div>
                  </ProgressSection>

                  <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
                    {registration.status === 'draft' ? (
                      <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                        <ContinueButton to={`/registration/${registration.id}`}>
                          Continue Editing
                        </ContinueButton>
                        <ViewButton to={`/my-requests/${registration.id}`}>
                          View Details
                        </ViewButton>
                      </div>
                    ) : (
                      <ViewButton to={`/my-requests/${registration.id}`}>
                        View Details
                      </ViewButton>
                    )}
                  </div>
                </RequestBody>
              </RequestCard>
            );
          })}
        </RequestsGrid>
      )}
    </Container>
  );
};

export default MyRequests;